import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { DialogProps } from "@radix-ui/react-dialog";
import { useTranslation } from "react-i18next";
import { useCallback, useEffect, useMemo, useState } from "react";
import { DownloadIcon, Loader2 } from "lucide-react";
import { Alert, AlertTitle, AlertDescription } from "../ui/alert";
import { useExportData } from "./hooks/useExportData";
import { EXPORT_FORMAT_CSV, EXPORT_FORMAT_XLSX } from "@/constants";
import { ExportDialogColumnSelection } from "./ExportDialogColumnSelection";
import type { ExportFeatures } from "@/types/exportFeatures";
import { buildExportParams } from "./utils/buildExportParams";

type EXPORT_FORMAT_TYPE = typeof EXPORT_FORMAT_CSV | typeof EXPORT_FORMAT_XLSX;

export interface ExportDialogProps extends DialogProps {
  moduleName: string;
  params?: URLSearchParams;
  allowedFormats?: EXPORT_FORMAT_TYPE[];
  exportFeatures?: ExportFeatures;
}

export default function ExportDialog({
  allowedFormats = [EXPORT_FORMAT_CSV, EXPORT_FORMAT_XLSX],
  exportFeatures,
  ...props
}: ExportDialogProps) {
  const { t } = useTranslation();
  const [format, setFormat] = useState<EXPORT_FORMAT_TYPE>(allowedFormats[0]);
  const [selectedColumns, setSelectedColumns] = useState<string[]>([]);

  const params = useMemo(
    () =>
      buildExportParams(
        props.params,
        selectedColumns,
        exportFeatures?.columnSelection,
      ),
    [props.params, selectedColumns, exportFeatures?.columnSelection],
  );

  const exportJob = useExportData({
    moduleName: props.moduleName,
    format,
    params,
    onSuccess: () => {
      props.onOpenChange?.(false);
    },
  });

  useEffect(() => {
    if (!props.open) {
      exportJob.reset();
      setSelectedColumns([]);
    }
  }, [exportJob.reset, props.open]);

  const handleExport = useCallback(() => {
    if (!props.moduleName) return;
    exportJob.mutate();
  }, [exportJob.mutate, props.moduleName]);

  const disabledXLSX = !allowedFormats.includes(EXPORT_FORMAT_XLSX);
  const disabledCSV = !allowedFormats.includes(EXPORT_FORMAT_CSV);

  const columnSelectionFeature = exportFeatures?.columnSelection;

  return (
    <Dialog {...props}>
      <DialogContent
        className="w-4/5 max-w-sm"
        onCloseAutoFocus={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>{t("exportDialog.title")}</DialogTitle>
        </DialogHeader>
        <article className="py-4">
          {exportJob.isError && (
            <Alert variant="destructive" className="mb-6">
              <AlertTitle>{t("exportDialog.errorTitle")}</AlertTitle>
              <AlertDescription>
                {exportJob.errorMessage ?? t("exportDialog.errorDescription")}
              </AlertDescription>
            </Alert>
          )}
          <p className="mb-6">{t("exportDialog.description")}</p>
          <div className="space-y-4">
            <label className="flex items-center space-x-2">
              <input
                type="radio"
                name="exportFormat"
                value={EXPORT_FORMAT_CSV}
                checked={format === EXPORT_FORMAT_CSV}
                onChange={() => setFormat(EXPORT_FORMAT_CSV)}
                className="h-4 w-4"
                disabled={disabledCSV || exportJob.isPending}
              />
              <span>CSV</span>
              {disabledCSV && (
                <span className="text-gray-500 text-sm">
                  {t("exportDialog.formatUnavailable")}
                </span>
              )}
            </label>
            <label className="flex items-center space-x-2">
              <input
                type="radio"
                name="exportFormat"
                value={EXPORT_FORMAT_XLSX}
                checked={format === EXPORT_FORMAT_XLSX}
                onChange={() => setFormat(EXPORT_FORMAT_XLSX)}
                className="h-4 w-4"
                disabled={disabledXLSX || exportJob.isPending}
              />
              <span>XLS</span>
              {disabledXLSX && (
                <span className="text-gray-500 text-sm">
                  {t("exportDialog.formatUnavailable")}
                </span>
              )}
            </label>
          </div>
          {columnSelectionFeature && (
            <ExportDialogColumnSelection
              feature={columnSelectionFeature}
              isExportPending={exportJob.isPending}
              onSelectedColumnsChange={setSelectedColumns}
            />
          )}
        </article>
        <DialogFooter className="gap-3">
          <DialogClose asChild>
            <Button variant="secondary">{t("exportDialog.cancel")}</Button>
          </DialogClose>
          <Button
            type="button"
            onClick={handleExport}
            disabled={exportJob.isPending}
            className="flex items-center"
          >
            {exportJob.isPending ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <DownloadIcon className="w-4 h-4 mr-2" />
            )}
            {t("exportDialog.download")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
